import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, MapPin } from "lucide-react";
import api, { imageUrl } from "../../lib/api";
import { formatINR } from "../../lib/constants";

const LocalitiesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/localities").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (l) => {
    setEditing(l.slug);
    setForm({
      name: l.name, city: l.city, headline: l.headline || "",
      overview: l.overview || "", image: l.image || "",
      highlights: (l.highlights || []).join("\n"),
    });
  };

  const save = async (e) => {
    e.preventDefault();
    await api.put(`/localities/${editing}`, {
      name: form.name, city: form.city, headline: form.headline,
      overview: form.overview, image: form.image,
      highlights: form.highlights.split("\n").map((h) => h.trim()).filter(Boolean),
    });
    toast.success("Locality guide saved");
    setEditing(null); setForm(null); load();
  };

  if (loading) return <div className="h-40 rounded-xl bg-stone-100 animate-pulse" />;

  return (
    <div data-testid="localities-panel">
      <p className="text-stone-600 text-sm mb-5">Edit the guide text buyers see on each area page. Stats update automatically from your listings.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {items.map((l) => (
          <div key={l.slug} className="bg-white rounded-2xl border border-stone-200 p-5" data-testid={`loc-row-${l.slug}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-stone-100 shrink-0">
                  {l.image && <img src={imageUrl(l.image)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div>
                  <p className="font-semibold text-stone-800 flex items-center gap-1"><MapPin size={14} className="text-brand" /> {l.name}</p>
                  <p className="text-xs text-stone-500">{l.count} listings · {formatINR(l.min_price)}+</p>
                </div>
              </div>
              <button onClick={() => startEdit(l)} data-testid={`edit-loc-${l.slug}`}
                className="p-2 rounded-lg hover:bg-stone-100 text-stone-600"><Pencil size={16} /></button>
            </div>
            {l.headline && <p className="text-sm text-stone-600 mt-3 line-clamp-2">{l.headline}</p>}
          </div>
        ))}
      </div>

      {editing && form && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto space-y-4" data-testid="loc-edit-form">
            <h3 className="font-heading text-2xl text-stone-900">Edit {form.name}</h3>
            <div>
              <label className="text-sm font-medium text-stone-700">Headline</label>
              <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} data-testid="loc-headline"
                className="mt-1.5 w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700">Overview</label>
              <textarea rows={5} value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} data-testid="loc-overview"
                className="mt-1.5 w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700">Highlights (one per line)</label>
              <textarea rows={4} value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} data-testid="loc-highlights"
                className="mt-1.5 w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700">Cover Image URL</label>
              <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="loc-image"
                placeholder="https://…" className="mt-1.5 w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
            <div className="flex gap-3">
              <button type="submit" data-testid="loc-save-btn" className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-dark transition-colors">Save</button>
              <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-stone-200 px-6 py-2.5 text-sm font-medium text-stone-600">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default LocalitiesPanel;
